import { useState, useRef, useEffect } from 'react'
import { Send, RefreshCw, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Markdown } from '@/features/workstation/components/ask/markdown'
import { motion, AnimatePresence } from 'framer-motion'
import { useAssistantEdit } from '../../context'
import { useAssistantChat } from '../../hooks/use-assistant-chat'
import LoadingChatMessage from './loading-chat-message'
import { shouldShowChatLoading } from '@/utils/chat-loading-detector'
import { getMessageText } from '@/features/workstation/utils/message-utils'

const MIN_HEIGHT = 40
const MAX_HEIGHT = 72
const LINE_HEIGHT = 24

interface ChatPreviewInteractiveProps {
  conversationId: string
  onReset?: () => void
  isEmbed?: boolean
}

const ChatPreviewInteractive = ({
  conversationId,
  onReset,
  isEmbed = false,
}: ChatPreviewInteractiveProps) => {
  const [inputRows, setInputRows] = useState(1)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const { editedAssistant, logoFile } = useAssistantEdit()

  const { messages, handleSubmit, input, setInput, isLoading, handleNewChat } =
    useAssistantChat({
      conversationId,
      foldersId:
        editedAssistant?.knowledge_base?.map((kb) => kb.folder_id) || [],
      assistantId: editedAssistant?.assistant_id,
      versionId: editedAssistant?.external_id,
      isEmbed,
      name: editedAssistant?.name,
      description: editedAssistant?.description,
      tools: editedAssistant?.tools || undefined,
      knowledge_base: editedAssistant?.knowledge_base || undefined,
    })

  const preferences = editedAssistant?.preferences

  const prefs = preferences
    ? {
        name: editedAssistant.name,
        logoUrl: logoFile ? URL.createObjectURL(logoFile) : editedAssistant.logo_url,
        statusMessage: preferences.typography.subtitle_message,
        greetingMessage: preferences.typography.greeting_message,
        inputPlaceholder: preferences.typography.placeholder_input_message,
        headerBgColor: preferences.interface.header_background_color,
        headerTextColor: preferences.interface.header_text_color,
        chatBgColor: preferences.interface.chat_background_color,
        chatBorderColor: preferences.interface.chat_border_color,
        agentBalloonBgColor: preferences.interface.agent_balloon_bg_color,
        agentBalloonTextColor: preferences.interface.agent_balloon_text_color,
        userBalloonBgColor: preferences.interface.user_balloon_bg_color,
        userBalloonTextColor: preferences.interface.user_balloon_text_color,
        inputBgColor: 'transparent',
        inputTextColor: preferences.interface.input_text_color || '#222222',
        inputBorderColor: preferences.interface.chat_border_color,
        placeholderColor: preferences.interface.chat_border_color,
      }
    : null

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (textareaRef.current) {
      if (!input) {
        setInputRows(1)
        textareaRef.current.style.height = `${MIN_HEIGHT}px`
        return
      }
      textareaRef.current.style.height = 'auto'
      const scrollHeight = textareaRef.current.scrollHeight
      let rows = Math.floor(scrollHeight / LINE_HEIGHT)
      if (rows < 1) rows = 1
      if (rows > 3) rows = 3
      setInputRows(rows)
      textareaRef.current.style.height = `${Math.min(scrollHeight, MAX_HEIGHT)}px`
    }
  }, [input])

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      const form = e.currentTarget.form
      if (form) {
        const event = new Event('submit', { bubbles: true, cancelable: true })
        form.dispatchEvent(event)
      }
    }
  }

  const handleUserMessageSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!input.trim()) return

    handleSubmit(e)
    setInputRows(1)
    if (textareaRef.current) {
      textareaRef.current.style.height = `${MIN_HEIGHT}px`
    }
  }

  if (!prefs) {
    return (
      <div className='flex h-full w-full items-center justify-center'>
        <div className='h-10 w-10 animate-spin rounded-full border-4 border-muted-foreground border-t-primary' />
      </div>
    )
  }

  return (
    <div
      className='flex h-full w-full flex-col overflow-hidden rounded-md shadow-lg'
      style={{
        background: prefs.chatBgColor,
      }}
    >
      <div
        className='flex items-center justify-between px-6 py-4'
        style={{
          background: prefs.headerBgColor,
          borderTop: `1px solid ${prefs.headerBgColor}`,
          borderLeft: `1px solid ${prefs.headerBgColor}`,
          borderRight: `1px solid ${prefs.headerBgColor}`,
          borderTopLeftRadius: '6px',
          borderTopRightRadius: '6px',
        }}
      >
        <div className='flex min-w-0 flex-1 items-center gap-3'>
          <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-foreground/20'>
            {prefs.logoUrl ? (
              <img
                src={prefs.logoUrl}
                alt={prefs.name}
                className='h-10 w-10 object-cover'
              />
            ) : (
              <span
                className='text-lg font-semibold'
                style={{ color: prefs.headerTextColor }}
              >
                {prefs.name[0]}
              </span>
            )}
          </div>
          <div className='min-w-0 flex-1'>
            <div
              className='truncate font-semibold'
              style={{ color: prefs.headerTextColor }}
            >
              {prefs.name}
            </div>
            <div
              className='truncate text-xs opacity-80'
              style={{ color: prefs.headerTextColor }}
            >
              {prefs.statusMessage}
            </div>
          </div>
        </div>
        <button
          type='button'
          onClick={() => {
            handleNewChat()
            onReset?.()
          }}
          className='ml-2 flex-shrink-0 rounded-full p-2 transition-colors hover:bg-white/10'
          style={{ color: prefs.headerTextColor }}
          aria-label='Reiniciar conversa'
        >
          <RefreshCw size={16} />
        </button>
      </div>
      <div
        className='flex min-h-0 flex-1 flex-col overflow-hidden'
        style={{
          background: prefs.chatBgColor,
        }}
      >
        <div className='min-h-0 flex-1 space-y-4 overflow-auto px-6 pb-4'>
          {messages.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className='mt-6 flex justify-start'
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2, delay: 0.1 }}
                className='max-w-[85%] rounded-3xl rounded-bl-none px-4 py-2 text-sm'
                style={{
                  background: prefs.agentBalloonBgColor,
                  color: prefs.agentBalloonTextColor,
                }}
              >
                {prefs.greetingMessage}
              </motion.div>
            </motion.div>
          ) : (
            <AnimatePresence>
          {messages
            .filter((msg) => getMessageText(msg).trim().length > 0)
              .map((msg, idx) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.95 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className={cn(
                    'flex',
                    msg.role === 'user' ? 'justify-end' : 'justify-start',
                    idx === 0 ? 'mt-6' : ''
                  )}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2, delay: 0.1 }}
                    className={cn(
                      'max-w-[85%] px-4 py-2 text-sm',
                      msg.role === 'user'
                        ? 'rounded-3xl rounded-br-none'
                        : 'rounded-3xl rounded-bl-none'
                    )}
                    style={{
                      background:
                        msg.role === 'user'
                          ? prefs.userBalloonBgColor
                          : prefs.agentBalloonBgColor,
                      color:
                        msg.role === 'user'
                          ? prefs.userBalloonTextColor
                          : prefs.agentBalloonTextColor,
                    }}
                  >
                    {msg.role === 'user' ? (
                      <div className='prose prose-sm dark:prose-invert max-w-none'>
                {getMessageText(msg)}
                      </div>
                    ) : (
                      <div className='prose prose-sm dark:prose-invert max-w-none'>
                        <Markdown>{getMessageText(msg)}</Markdown>
                      </div>
                    )}
                  </motion.div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
          {shouldShowChatLoading(messages, isLoading) && (
            <motion.div 
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className='flex justify-start'
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2, delay: 0.1 }}
                className='max-w-[85%] rounded-3xl rounded-bl-none px-4 py-2 text-sm'
                style={{
                  background: prefs.agentBalloonBgColor,
                  color: prefs.agentBalloonTextColor,
                }}
              >
                <LoadingChatMessage color={prefs.agentBalloonTextColor} />
              </motion.div>
            </motion.div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
      <div
        className='rounded-b-md border p-4'
        style={{
          borderTop: `1px solid ${prefs.chatBorderColor}`,
          borderLeft: `none`,
          borderRight: `none`,
          borderBottom: `none`,
          background: prefs.chatBgColor,
        }}
      >
        <form onSubmit={handleUserMessageSubmit}>
          <div className='relative flex items-end gap-2 rounded-md'>
            <textarea
              ref={textareaRef}
              className='flex-1 resize-none rounded-lg border bg-transparent px-3 py-2 pr-10 focus-visible:outline-none focus-visible:ring-0 text-sm'
              rows={inputRows}
              placeholder={isLoading ? 'Pensando...' : prefs.inputPlaceholder}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleInputKeyDown}
              disabled={isLoading}
              style={{
                minHeight: `${MIN_HEIGHT}px`,
                maxHeight: `${MAX_HEIGHT}px`,
                overflow: inputRows === 3 ? 'auto' : 'hidden',
                paddingRight: '2.5rem',
                background: prefs.inputBgColor,
                borderColor: prefs.chatBorderColor,
                color: prefs.inputTextColor,
              }}
            />
            <style>{`
              textarea::placeholder {
                font-size: 0.9rem;
              }
            `}</style>
            {input.trim() && !isLoading && (
              <button
                type='submit'
                className='absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 transition-colors'
                tabIndex={0}
                aria-label='Enviar'
                style={{ color: prefs.inputTextColor }}
                disabled={isLoading || !input.trim().length}
              >
                <Send size={18} />
              </button>
            )}
            {isLoading && (
              <div className='absolute right-3 top-1/2 -translate-y-1/2'>
                <Loader2 className='h-4 w-4 animate-spin' style={{ color: prefs.headerBgColor }} />
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default ChatPreviewInteractive
